import { useNavigation } from '@react-navigation/native';
import { observer } from 'mobx-react-lite';
import React, { Fragment, useCallback } from 'react';
import { SxProp } from 'dripsy';

import { screens, TabNavigatorNavigationPropType } from '@navigation';
import { TutorialExtraCategoryIdEnum } from '@constants';
import { IS_WEB, passIf } from '@utils';
import {
  EmptyResultTypeEnum,
  Separator,
  TutorialList as BaseTutorialList,
} from '@components';
import type { Tutorial } from '@models/Tutorials/TutorialModel';
import type { TutorialList } from '@models/Tutorials/TutorialListModel';
import { TutorialListHeader } from './TutorialListHeader';
import { AnalyticsService } from '@services';

const contentContainerStyle: SxProp = {
  flexGrow: 1,
  px: ['$6', '$0'],
};

interface IHubTutorialListProps {
  list: TutorialList;
  fetchMore(): Promise<void>;
  refresh(): Promise<void>;
}

type NavigationPropType = TabNavigatorNavigationPropType<'Hub'>;

export const HubTutorialList = observer<IHubTutorialListProps>(
  ({ list, fetchMore, refresh }) => {
    const nav = useNavigation<NavigationPropType>();

    function trackTutorialAnalytic(item: Tutorial) {
      AnalyticsService.trackHubContent(item.title);
    }

    function navigateToTutorial(item: Tutorial) {
      nav.push(screens.HubNavigator, {
        screen: screens.Tutorial,
        params: {
          id: item.id,
          extraCategoryId: passIf(
            !list.hasActiveFilter,
            TutorialExtraCategoryIdEnum.All,
          ),
        },
      });
    }

    const onTutorialPress = useCallback(
      (item: Tutorial) => {
        navigateToTutorial(item);
        trackTutorialAnalytic(item);
      },
      [list.hasActiveFilter],
    );

    const isLoading = list.fetch.inProgress && list.isEmpty;

    let emptyResultType: EmptyResultTypeEnum =
      EmptyResultTypeEnum.Error;

    if (list.fetch.isError) {
      emptyResultType = EmptyResultTypeEnum.Error;
    } else if (list.hasActiveFilter) {
      emptyResultType = EmptyResultTypeEnum.NoResult;
    }

    return (
      <BaseTutorialList
        contentContainerSx={contentContainerStyle}
        data={list.asArray}
        onEndReached={fetchMore}
        onRefresh={refresh}
        isRefreshing={list.fetch.inProgress}
        isLoading={isLoading}
        isLoadingMore={list.fetchMore.inProgress}
        emptyResultType={emptyResultType}
        onPress={onTutorialPress}
        ListHeaderComponent={() => (
          <Fragment>
            {IS_WEB && <TutorialListHeader />}
            <Separator size="$8" />
          </Fragment>
        )}
      />
    );
  },
);
